import React, { useState, useCallback } from 'react';
import {
    Modal,
    SpaceBetween,
    Button,
    Box,
    Alert,
    Spinner
} from '@amzn/awsui-components-react';
import dayjs from 'dayjs';
import axios from 'axios';

import HtmlRenderer from '../../../tiptap/HtmlRenderer';
import TextEditor from '../../../tiptap/TipTap';

import { CoachingCategory, CoachingRecord, CoachingStatus } from '../types';
import { useContinuousAuth } from '../../../pages/App';
import { UserPermissions } from '../../../common/UserPermissions';
import settings from '../../../settings.json';
import Styled from './styled';

const settingEnv = (settings as any)[window.location.host] || settings.default;
const { api_prefix: apiPrefix } = settingEnv;

const defaultEditorValue = '<p> </p>';
const currentDate = dayjs().format('YYYY-MM-DD');
const dueDate = dayjs().add(5, 'days').format('YYYY-MM-DD');

interface ViewDetailsModalProps {
    loading: boolean;
    visible: boolean;
    onDismiss: () => void;
    selectedCoaching: CoachingRecord | null;
    userPermissions: Set<string>;
    enableCsqaIntegration: boolean;
    refreshData: () => void;
    addErrorMessage: (msg: string) => void;
    addSuccessMessage: (msg: string) => void;
}

const ViewDetailsModal: React.FC<ViewDetailsModalProps> = ({
    loading,
    visible,
    onDismiss,
    selectedCoaching,
    userPermissions,
    enableCsqaIntegration,
    refreshData,
    addErrorMessage,
    addSuccessMessage
}) => {

    // If coaching details are not yet available, show the loading indicator
    if (loading || !selectedCoaching) {
        return (
            <Modal
                visible={visible}
                onDismiss={onDismiss}
                header='Coaching Details'
                footer={<Button onClick={onDismiss}>Close</Button>}
            >
                <Styled.LoadingWrapper>
                    <Spinner size='large' />
                </Styled.LoadingWrapper>
            </Modal>
        );
    }

    const { getHeaders } = useContinuousAuth();
    const [attachmentLoading, setAttachmentLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);

    const [actionToConfirm, setActionToConfirm] = useState<
        '' | 'approve' | 'deliver' | 'delete'
    >('');
    const [reworkType, setReworkType] = useState<'hr' | 'cs' | null>(null);
    const [reworkNotes, setReworkNotes] = useState('');

    const updateCoaching = useCallback(
        async (updatedDoc: Partial<CoachingRecord>, updateStatus: string) => {
            setUpdateLoading(true);
            try {
                const { id } = selectedCoaching;
                const payload = {
                    doc: {
                        ...selectedCoaching,
                        ...updatedDoc,
                    },
                    id,
                };

                const headers = await getHeaders();
                await axios.post(`${apiPrefix}/api/coaching-tool`, payload, { headers });

                addSuccessMessage(`Coaching updated: ${updateStatus}`);

                refreshData();
                onDismiss();
            } catch (e: any) {
                addErrorMessage(e.message || 'Update failed');
            } finally {
                setUpdateLoading(false);
            }
        },
        [
            selectedCoaching,
            onDismiss,
            refreshData,
            addErrorMessage,
            addSuccessMessage,
            getHeaders
        ]
    );

    const updateCsqaCoachingStatus = useCallback(
        async (request: any) => {
            setUpdateLoading(true);
            try {
                const headers = await getHeaders();
                await axios.post(`${apiPrefix}/api/csqa`, request, { headers });
            } catch (e: any) {
                addErrorMessage('There was a problem with your Request');
            } finally {
                setUpdateLoading(false);
            }
        },
        [getHeaders, addErrorMessage]
    );

    const handleAction = useCallback(async () => {
        if (!actionToConfirm) return;

        switch (actionToConfirm) {
            // ------------------------ APPROVE ------------------------
            case 'approve': {
                const approvalTime = dayjs();
                const creationTime = dayjs.unix(selectedCoaching.createdTimestamp as number);
                const pendingTime = selectedCoaching.csReworkDate
                    ? dayjs(selectedCoaching.csReworkDate)
                    : creationTime;

                const pendingToReadyForDeliveryTime = approvalTime.diff(pendingTime);
                const creationToReadyForDeliveryTime = approvalTime.diff(creationTime);

                await updateCoaching(
                    {
                        dueDate,
                        status: 'Ready for Delivery',
                        approvalDate: approvalTime.toISOString(),
                        pendingToReadyForDeliveryTime,
                        creationToReadyForDeliveryTime,
                    },
                    'Approved'
                );
                break;
            }

            // ------------------------ DELIVER ------------------------
            case 'deliver': {
                const deliverTime = dayjs();
                const approvalOrCreated = selectedCoaching.approvalDate
                    ? dayjs(selectedCoaching.approvalDate)
                    : dayjs(selectedCoaching.createdDate);

                const readyForDeliveryToDeliveryTime = Math.abs(
                    deliverTime.diff(approvalOrCreated)
                );

                const updatedDoc = {
                    dueDate: currentDate,
                    status: 'Delivered',
                    deliverDate: deliverTime.toISOString(),
                    readyForDeliveryToDeliveryTime,
                } as Partial<CoachingRecord>;

                // Update CSQA if integration is on
                if (enableCsqaIntegration && selectedCoaching.csqaReviewId) {
                    await updateCsqaCoachingStatus({
                        updateType: 'coaching status',
                        csqaReviewId: selectedCoaching.csqaReviewId,
                        createdTimestamp: selectedCoaching.createdTimestamp,
                    });
                }

                await updateCoaching(updatedDoc, 'Delivered');
                break;
            }

            // ------------------------ DELETE ------------------------
            case 'delete': {
                await updateCoaching(
                    {
                        status: CoachingStatus.Deleted,
                        deleted: true,
                    },
                    'Deleted'
                );
                break;
            }
        }

        setActionToConfirm('');
    }, [
        actionToConfirm,
        updateCoaching,
        updateCsqaCoachingStatus,
        enableCsqaIntegration,
        selectedCoaching,
    ]);

    const handleRework = useCallback(() => {
        if (!reworkNotes || reworkNotes === defaultEditorValue) {
            addErrorMessage('Please add Rework notes');
            return;
        }
        const now = dayjs();

        if (reworkType === 'hr') {
            // HR rework: 'Pending' => 'Rework'
            const pendingTime = selectedCoaching.csReworkDate
                ? dayjs(selectedCoaching.csReworkDate)
                : dayjs.unix(selectedCoaching.createdTimestamp as number);

            const pendingToReworkTime = selectedCoaching.pendingToReworkTime
                ? selectedCoaching.pendingToReworkTime + now.diff(pendingTime)
                : now.diff(pendingTime);

            updateCoaching(
                {
                    dueDate,
                    hrNotes: reworkNotes,
                    hrReworkDate: now.toISOString(),
                    status: 'Rework',
                    pendingToReworkTime,
                },
                'Rework'
            );
        } else if (reworkType === 'cs') {
            // CS rework: 'Rework' => 'Pending'
            const reworkTime = dayjs(selectedCoaching.hrReworkDate);
            const reworkToPendingTime = selectedCoaching.reworkToPendingTime
                ? selectedCoaching.reworkToPendingTime + now.diff(reworkTime)
                : now.diff(reworkTime);

            updateCoaching(
                {
                    dueDate,
                    notes: reworkNotes,
                    csReworkDate: now.toISOString(),
                    status: 'Pending',
                    reworkToPendingTime,
                },
                'CS Rework'
            );
        }

        setReworkNotes('');
        setReworkType(null);
    }, [
        reworkType,
        reworkNotes,
        selectedCoaching,
        addErrorMessage,
        updateCoaching,
    ]);

    const handleDownloadAttachment = useCallback(async () => {
        if (!selectedCoaching.file?.key) return;
        setAttachmentLoading(true);

        try {
            const headers = await getHeaders();
            const { key } = selectedCoaching.file;
            const { id, createdTimestamp } = selectedCoaching;

            const response = await axios.get(`${apiPrefix}/api/coaching-attachment`, {
                headers,
                params: { key, id, createdTimestamp },
            });
            const url = response.data;
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.download = 'download.file';
            a.click();
        } catch (e: any) {
            addErrorMessage('There was a problem with your Request');
        } finally {
            setAttachmentLoading(false);
        }
    }, [selectedCoaching, getHeaders, addErrorMessage]);

    const renderActionConfirmation = () => {
        if (!actionToConfirm) return null;

        let confirmMessage = '';
        switch (actionToConfirm) {
            case 'approve':
                confirmMessage =
                    'Selecting confirm will Approve this incident for CS Leadership.';
                break;
            case 'deliver':
                confirmMessage =
                    'Selecting confirm will deliver the incident to the Agent & Supervisor.';
                break;
            case 'delete':
                confirmMessage =
                    'Selecting confirm will permanently delete this incident.';
                break;
            default:
                break;
        }

        return (
            <Box margin={{ top: 's', bottom: 'm' }}>
                <Alert type='info' header='Are you sure?'>
                    <SpaceBetween direction='vertical' size='s'>
                        {confirmMessage}
                        <SpaceBetween direction='horizontal' size='s'>
                            <Button variant='primary' onClick={handleAction}>
                                Confirm
                            </Button>
                            <Button onClick={() => setActionToConfirm('')}>Cancel</Button>
                        </SpaceBetween>
                    </SpaceBetween>
                </Alert>
            </Box>
        );
    };

    return (
        <Modal
            visible={visible}
            size='max'
            onDismiss={onDismiss}
            header='Coaching Details'
            footer={
                <SpaceBetween direction='vertical' size='m'>
                    {/* Inline confirmation (Approve/Deliver/Delete) if triggered */}
                    {renderActionConfirmation()}
                    <SpaceBetween direction='horizontal' size='m'>
                        {/* PENDING => HR can Approve / Rework / Delete */}
                        {selectedCoaching.status === CoachingStatus.Pending &&
                            userPermissions.has(UserPermissions.ReworkCoaching) && (
                                <>
                                    <Button
                                        onClick={() => setActionToConfirm('approve')}
                                        loading={updateLoading}
                                        variant='primary'
                                    >
                                        Approve
                                    </Button>
                                    <Button onClick={() => setReworkType('hr')}>
                                        HR Rework
                                    </Button>
                                    <Button
                                        onClick={() => setActionToConfirm('delete')}
                                        loading={updateLoading}
                                    >
                                        Delete
                                    </Button>
                                </>
                            )}

                        {/* REWORK => CS can update => Rework => Pending */}
                        {selectedCoaching.status === CoachingStatus.Rework &&
                            userPermissions.has(UserPermissions.EditCoaching) && (
                                <>
                                    <Button onClick={() => setReworkType('cs')}>
                                        CS Rework
                                    </Button>
                                    <Button
                                        onClick={() => setActionToConfirm('delete')}
                                        loading={updateLoading}
                                    >
                                        Delete
                                    </Button>
                                </>
                            )}

                        {/* READY FOR DELIVERY => Can Deliver */}
                        {selectedCoaching.status === CoachingStatus.ReadyForDelivery &&
                            userPermissions.has(UserPermissions.DeliverCoaching) && (
                                <Button
                                    onClick={() => setActionToConfirm('deliver')}
                                    loading={updateLoading}
                                    variant='primary'
                                >
                                    Deliver
                                </Button>
                            )}

                        <Button onClick={onDismiss}>Close</Button>
                    </SpaceBetween>
                </SpaceBetween>
            }
        >
            {/* Coaching Details */}
            <Box margin={{ bottom: 'm' }}>
                <strong>Form ID:</strong> {selectedCoaching.formId || 'N/A'}
            </Box>
            <Box margin={{ bottom: 'm' }}>
                <strong>Agent Email:</strong> {selectedCoaching.agent || 'N/A'}
            </Box>
            <Box margin={{ bottom: 'm' }}>
                <strong>Supervisor Email:</strong> {selectedCoaching.supervisor || 'N/A'}
            </Box>
            <Box margin={{ bottom: 'm' }}>
                <strong>Salesforce URL:</strong>{' '}
                {selectedCoaching.kustomerUrl ? (
                    <a href={selectedCoaching.kustomerUrl} target='_blank' rel='noreferrer'>
                        {selectedCoaching.kustomerUrl}
                    </a>
                ) : (
                    'N/A'
                )}
            </Box>
            <Box margin={{ bottom: 'm' }}>
                <strong>Date:</strong> {selectedCoaching.date || 'N/A'}
            </Box>
            <Box margin={{ bottom: 'm' }}>
                <strong>Category:</strong> {selectedCoaching.category || 'N/A'}
            </Box>
            <Box margin={{ bottom: 'm' }}>
                <strong>Reason:</strong> {selectedCoaching.reason || 'N/A'}
            </Box>
            <Box margin={{ bottom: 'm' }}>
                <strong>Action:</strong> {selectedCoaching.action || 'N/A'}
            </Box>
            <Box margin={{ bottom: 'm' }}>
                <strong>Status:</strong> {selectedCoaching.status}
            </Box>
            <Box margin={{ bottom: 'm' }}>
                <strong>Notes:</strong> <HtmlRenderer html={selectedCoaching.notes || 'N/A'} />
            </Box>

            {/* HR & Additional Fields (Permission Based) */}
            {selectedCoaching.hrbp && userPermissions.has(UserPermissions.HrViewCoaching) && (
                <Box margin={{ bottom: 'm' }}>
                    <strong>HRBP Associated:</strong> {selectedCoaching.hrbp}
                </Box>
            )}

            {selectedCoaching.hrNotes && userPermissions.has(UserPermissions.HrViewCoaching) && (
                <Box margin={{ bottom: 'm' }}>
                    <strong>HR Notes:</strong>
                    <HtmlRenderer html={selectedCoaching.hrNotes} />
                </Box>
            )}

            {selectedCoaching.additionalNotes && userPermissions.has(UserPermissions.HrViewCoaching) && (
                <Box margin={{ bottom: 'm' }}>
                    <strong>Additional Notes (Non-Agent Facing):</strong>
                    <HtmlRenderer html={selectedCoaching.additionalNotes} />
                </Box>
            )}
            {/* Download Attachment (permission based) */}
            {selectedCoaching.file?.key && (
                (userPermissions.has(UserPermissions.HrViewCoaching) ||
                    selectedCoaching.category === CoachingCategory.Coaching) && (
                    <Box margin={{ bottom: 'm' }}>
                        <Button 
                            onClick={handleDownloadAttachment} 
                            loading={attachmentLoading}
                        >
                            Download Attachment
                        </Button>
                    </Box>
                )
            )}

            {reworkType && (
                <Modal
                    visible={!!reworkType}
                    onDismiss={() => {
                        setReworkType(null);
                        setReworkNotes('');
                    }}
                    header={reworkType === 'hr' ? 'Rework Details (HR)' : 'Rework Details (CS)'}
                    footer={
                        <SpaceBetween direction='horizontal' size='m'>
                            <Button
                                onClick={() => {
                                    setReworkType(null);
                                    setReworkNotes('');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleRework} variant='primary'>
                                Submit Rework
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <Box margin={{ bottom: 'm' }}>
                        {reworkType === 'hr'
                            ? 'Please provide supporting reasoning behind the request for CS Leadership to rework this incident.'
                            : 'Please update your note details below to reflect the requested changes by your HRBP.'}
                    </Box>
                    <TextEditor
                        value={reworkNotes}
                        onChange={(value: string) => setReworkNotes(value)}
                    />
                    {reworkType === 'cs' && (
                        <Box margin={{ top: 'm' }}>
                            <strong>Previous Details:</strong>
                            <Box variant='div' margin={{ top: 'xs' }}>
                                <HtmlRenderer html={selectedCoaching.notes || 'N/A'} />
                            </Box>
                        </Box>
                    )}
                </Modal>
            )}
        </Modal>
    );
};

export default ViewDetailsModal;
